import React, {FC, useEffect, useState} from "react"

import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import History from "@irati/utils/history"
import id from "@irati/utils/id"
import {Body, H2, H3} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import Link, {ButtonRouterLink} from "@irati/ui/src/Link"
import GenericModal from "@irati/ui/src/GenericModal"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"

import LoginBlock from "../LoginBlock"
import LoginBackground from "../LoginBackground"
import {HeaderLogo} from "./styled"

interface LocationProps extends InjectIntl {
  subdomain?: boolean
  onboarding?: boolean
  email?: string
}

const ELAPSE_TIME_BEFORE_WARNING = 1800000 // 30 minutes

const CheckEmail: FC<LocationProps> = ({formatMessage, location}) => {
  const idNamespace = id("login-check-email")
  const {email, onboarding, subdomain} = location
  const [expired, setExpired] = useState<boolean>(false)

  useEffect(() => {
    // subdomain reminder email has no expiry, so don't set the timer
    if (!subdomain) {
      const timer = setTimeout(() => setExpired(true), ELAPSE_TIME_BEFORE_WARNING)
      return () => clearTimeout(timer)
    }
    return () => {}
  }, [])

  const backToLogin = () => History.push("welcome/login")

  const retry = () => History.push("welcome/login/forgotten-password")

  const bodyText = () => {
    if (subdomain) {
      return "platform.checkEmail.subdomain1"
    } else if (onboarding) {
      return "platform.checkEmail.onboarding1"
    }
    return "platform.checkEmail.resetBody"
  }

  const headerText = () => {
    if (subdomain) {
      return "platform.checkEmail.header.subdomain"
    }
    if (onboarding) {
      return "platform.checkEmail.header.onboardEmail"
    }
    return "platform.checkEmail.header.passwordReset1"
  }

  let helpLinks
  if (subdomain) {
    helpLinks = [
      <Link href={formatMessage({id: "platform.checkEmail.help.domainUrl"})} target="_blank">
        <FormattedMessage id="platform.checkEmail.help.domainLink" />
      </Link>
    ]
  } else if (!onboarding) {
    helpLinks = [
      <Link href={formatMessage({id: "platform.checkEmail.help.forgotPasswordUrl"})} target="_blank">
        <FormattedMessage id="platform.checkEmail.help.helpLink" />
      </Link>
    ]
  }

  return (
    <LoginBackground>
      <LoginBlock id={idNamespace} helpLinks={helpLinks}>
        <Flex marginBottom={1}>
          <Button tertiary onClick={subdomain || onboarding ? backToLogin : retry}>
            <Icon icon={IconTypes.angleLeft} marginRight />
            <FormattedMessage id="platform.forgotPassword.resetPassword.back" />
          </Button>
        </Flex>
        <Flex justifyContent="center" alignContent="center" marginTop>
          <HeaderLogo src={formatMessage({id: "platform.checkEmail.header.logo"})} />
        </Flex>
        <H3 marginBottom={2}>
          <FormattedMessage id={headerText()} />
        </H3>
        <Body marginBottom={3} width="90%">
          <FormattedMessage
            id={bodyText()}
            values={{
              email: (
                <Body display="inline" weight="bold">
                  {email}
                </Body>
              ),
              redirect: (
                <ButtonRouterLink small to="welcome/login" cta tertiary>
                  <FormattedMessage id="platform.checkEmail.redirect" />
                </ButtonRouterLink>
              )
            }}
          />
        </Body>
      </LoginBlock>
      {expired && (
        <GenericModal
          show
          onHide={() => setExpired(false)}
          actions={
            <Flex justifyContent="flex-end" alignItems="center" marginTop={5}>
              <Button marginRight={2} tertiary onClick={backToLogin}>
                <FormattedMessage id="platform.checkEmail.help.backToLogin" />
              </Button>
              <Button primary onClick={retry}>
                <FormattedMessage id="platform.checkEmail.help.again" />
              </Button>
            </Flex>
          }>
          <H2 marginBottom={1}>
            <FormattedMessage id="platform.checkEmail.help.linkExpiredHeader" />
          </H2>
        </GenericModal>
      )}
    </LoginBackground>
  )
}

export default injectIntl(CheckEmail)
